// src/pages/Admin/Views/SellerManagement/LeadHistory/LeadHistoryTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { getSellerLedger, initiateLeadRefund } from '../../../../services/AdminApis';
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "../../../../components/ui/alert";
import { DataTable } from './DataTable';
import { columns } from './Columns';
import RefundModal from './RefundModal';
import { useToast } from '../../../../hooks/use-toast';
import { AlertTriangle } from 'lucide-react';
import { usePermissions } from '../../../../hooks/usePermissions';

// --- NEW IMPORT ---
// Adjust this path if you saved the component in a different location [cite: 30]
import LeadReturnStatsTable from './LeadReturnStatsTable'; 

const LeadHistoryTab = ({ sellerProfileId }) => {
  const [data, setData] = useState({ items: [], total_pages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const { toast } = useToast();
  const { hasPermission } = usePermissions();

  const canReturnCoins = hasPermission('cx:return-lead-coins');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log("Seller Profile ID:",sellerProfileId);
      const result = await getSellerLedger(sellerProfileId, pagination.pageIndex + 1, pagination.pageSize);
      setData({ items: result.items, total_pages: result.total_pages });
    } catch (err) {
      setError("Failed to fetch transaction history.");
    } finally {
      setIsLoading(false);
    }
  }, [sellerProfileId, pagination]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefundClick = (rowData) => {
    setRefundTarget(rowData);
    setIsRefundModalOpen(true);
  };

  const handleRefundSubmit = async (refundData) => {
    try {
      await initiateLeadRefund(refundData);
      toast({
        variant: "success",
        title: "Refund Successful",
        description: "The seller's coin balance has been updated.",
      });
      fetchData(); // Refresh data after refund
      return true; // Indicate success to close modal
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "An unexpected error occurred.";
      toast({
        variant: "destructive",
        title: "Refund Failed",
        description: errorMessage,
      });
      return false; // Indicate failure
    }
  };

  const dynamicColumns = columns({ onRefundClick: handleRefundClick, canReturnCoins: canReturnCoins });

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      {/* --- NEW SECTION: Lead Return Analytics --- */}
      <div className='pb-4'>
      <Card>
        <CardHeader>
          <CardTitle>Lead Return Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <LeadReturnStatsTable sellerProfileId={sellerProfileId} />
        </CardContent>
      </Card>
      {/* --- END OF NEW SECTION --- */}
      </div>

      <div>
      <Card>
        <CardHeader>
          <CardTitle>Transaction Ledger</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={dynamicColumns}
            data={data.items}
            isLoading={isLoading}
            pageCount={data.total_pages}
            pagination={pagination}
            setPagination={setPagination}
          />
        </CardContent>
      </Card>
      </div>
      
      {refundTarget && (
        <RefundModal
          key={refundTarget.transaction_id}
          isOpen={isRefundModalOpen}
          onClose={() => setIsRefundModalOpen(false)}
          onSubmit={handleRefundSubmit}
          transaction_id={refundTarget.transaction_id}
          description={refundTarget.description}
        />
      )}
    </>
  );
};

export default LeadHistoryTab;